/**
 * Adapted Table component.
 *
 * Tables always require inputDataset for data lookup - they cannot use inline data.
 * Uses useToolOutputData to get raw tool output, then transforms via createTableAdapter.
 */

import { useAuiState } from '@assistant-ui/react';
import type z from 'zod';

import type { TableSchemaOutput } from '#core/components/dashboard/panels/table/schema.ts';
import { Table } from '#core/components/dashboard/panels/table/Table.component.tsx';
import { getComponentSkeleton } from '#core/components/dashboard/registry.ts';

import { BlockError } from '#plugin-chat/components/shared/BlockError.tsx';
import { createTableAdapter } from '#plugin-chat/components/tools/show-dashboard/helpers.ts';
import type { LlmGridItemSchema } from '#plugin-chat/components/tools/show-dashboard/schema.ts';
import { useDataset } from '#plugin-chat/hooks/useDataset.ts';

export interface AdaptedTableProps {
  item: z.infer<typeof LlmGridItemSchema> & { component: { type: 'table' } };
}

export function AdaptedTable({ item }: AdaptedTableProps) {
  const inputDataset = item.component.inputDataset;
  const isThreadRunning = useAuiState(({ thread }) => thread.isRunning);
  const dataset = useDataset(inputDataset);
  const output = dataset ? createTableAdapter(dataset) : undefined;

  if (isThreadRunning && !output?.records.length) {
    const Skeleton = getComponentSkeleton('table');
    if (!Skeleton) {
      return null;
    }
    return <Skeleton />;
  }

  if (!inputDataset || !output?.records.length) {
    return <BlockError blockType="Table" message="Table requires inputDataset pointing at a dataset" />;
  }

  // Use LLM-specified columns if available, fall back to adapter-resolved from dataset schema
  const llmColumns = item.component?.columns as TableSchemaOutput['columns'];
  const llmTitle = item.component?.title as TableSchemaOutput['title'];

  return (
    <Table
      columns={llmColumns ?? output.columns}
      records={output.records as Record<string, unknown>[]}
      title={item.caption ?? llmTitle}
    />
  );
}
